<script setup>
/**
 * BaileysAttributeItem.vue
 * Componente para exibir e editar atributos de configuracao do Baileys
 * Segue o estilo do CustomAttributesEvolutionItem
 */
import { ref, watch } from 'vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';

const props = defineProps({
  attribute: {
    type: Object,
    required: true,
    // Expected shape:
    // { key: string, label: string, icon: string, type: 'checkbox' | 'number',
    //   value: boolean | number, min?: number, max?: number, showIf?: string, hasMessage?: boolean }
  },
  modelValue: {
    type: [Boolean, Number, String],
    default: false,
  },
  msgCallValue: {
    type: String,
    default: '',
  },
  disabled: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['update:modelValue', 'update:msgCallValue', 'change', 'blur-msg', 'blur-number']);

// Local value for switch
const switchValue = ref(Boolean(props.modelValue));
const numberValue = ref(props.modelValue);
const msgCallLocal = ref(props.msgCallValue);

// Watch for external changes
watch(() => props.modelValue, (newVal) => {
  if (props.attribute.type === 'checkbox') {
    switchValue.value = Boolean(newVal);
  } else {
    numberValue.value = newVal;
  }
});

watch(() => props.msgCallValue, (newVal) => {
  msgCallLocal.value = newVal;
});

// Handle switch change
const handleSwitchChange = (value) => {
  emit('update:modelValue', value);
  emit('change', { key: props.attribute.key, value });
};

// Handle number input change
const handleNumberBlur = () => {
  let validValue = numberValue.value;
  if (props.attribute.min !== undefined) {
    validValue = Math.max(props.attribute.min, validValue);
  }
  if (props.attribute.max !== undefined) {
    validValue = Math.min(props.attribute.max, validValue);
  }
  numberValue.value = validValue;
  emit('update:modelValue', validValue);
  emit('blur-number', { key: props.attribute.key, value: validValue });
};

// Handle message blur
const handleMsgBlur = () => {
  emit('update:msgCallValue', msgCallLocal.value);
  emit('blur-msg', { key: props.attribute.key, value: msgCallLocal.value });
};

// Icon mapping based on attribute key
const getSpecificIcon = (attr) => {
  const iconMap = {
    rejectCall: '📵',
    receiveGroups: '👥',
    alwaysOnline: '🟢',
    readMessages: '✅',
    importMessageHistory: '🔄',
    historyDaysLimit: '📅',
    readStatus: '👁️',
    signMsg: '✍️',
  };
  return attr.icon || iconMap[attr.key] || '⚙️';
};
</script>

<template>
  <div class="max-w-7xl w-full flex flex-col">
    <!-- Main row with switch/input -->
    <div
      class="grid grid-cols-[1fr,auto] group/attribute items-center w-full gap-4 p-2 hover:bg-slate-50 dark:hover:bg-slate-800/50 rounded-lg transition-colors min-h-11"
    >
      <div class="flex items-center gap-3 min-w-0">
        <!-- Icon -->
        <span class="text-lg flex-shrink-0" :title="attribute.key">
          {{ getSpecificIcon(attribute) }}
        </span>

        <!-- Label -->
        <div class="min-w-0 flex-1">
          <span class="text-sm font-medium text-slate-900 dark:text-slate-100 block">
            {{ attribute.label }}
          </span>
        </div>
      </div>

      <!-- Control: Switch or Number input -->
      <div class="flex-shrink-0 flex items-center">
        <!-- Checkbox type uses Switch -->
        <Switch
          v-if="attribute.type === 'checkbox'"
          v-model="switchValue"
          :disabled="disabled"
          @change="handleSwitchChange"
        />

        <!-- Number type uses input -->
        <div v-else-if="attribute.type === 'number'" class="flex items-center gap-1.5 flex-nowrap mt-3">
          <input
            v-model.number="numberValue"
            type="number"
            :min="attribute.min"
            :max="attribute.max"
            :disabled="disabled"
            v-tooltip.right="attribute.max ? `Max: ${attribute.max} dias` : ''"
            class="w-14 px-2 py-1 text-sm text-center border border-slate-300 dark:border-slate-600 rounded-md
                   focus:ring-woot-500 focus:border-woot-500
                   bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100
                   disabled:opacity-50 disabled:cursor-not-allowed"
            @change="handleNumberBlur"
          />

        </div>
      </div>
    </div>

    <!-- Conditional message input (for rejectCall) -->
    <div v-if="attribute.hasMessage && switchValue" class="mt-2 ml-8">
      <textarea
        v-model="msgCallLocal"
        :placeholder="'Mensagem que sera enviada ao rejeitar chamada...'"
        :disabled="disabled"
        rows="2"
        class="w-full max-w-7xl p-2 text-sm border border-slate-300 dark:border-slate-600 rounded-md
               bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100
               placeholder-slate-400 dark:placeholder-slate-500
               focus:ring-2 focus:ring-woot-500 focus:border-woot-500
               transition-colors resize-none
               disabled:opacity-50 disabled:cursor-not-allowed"
        @blur="handleMsgBlur"
      />
      <p class="text-xs text-slate-500 dark:text-slate-400 mt-1">
        Esta mensagem sera enviada quando uma chamada for rejeitada
      </p>
    </div>
  </div>
</template>

<style scoped>
/* Hover styles */
.group\/attribute:hover {
  @apply bg-slate-50 dark:bg-slate-800/50;
}

/* Focus styles for inputs */
input:focus,
textarea:focus {
  @apply outline-none;
}

/* Responsive adjustments */
@media (max-width: 640px) {
  .text-sm {
    @apply text-xs;
  }

  .text-lg {
    @apply text-base;
  }
}
</style>
